import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type IdlePolicy = { warning_seconds: number; stop_seconds: number; keep_mode: 'prompt' | 'always' | 'never' };
// Keep the new RPC contract scoped until the generated schema is refreshed.
const policyRpc = supabase.rpc.bind(supabase) as unknown as (name: string, args?: Record<string, unknown>) =>
  PromiseLike<{ data: unknown; error: { message: string } | null }>;
function readPolicy(value: unknown): IdlePolicy {
  const policy = value as IdlePolicy;
  if (!policy || !Number.isInteger(policy.warning_seconds) || !Number.isInteger(policy.stop_seconds) ||
    policy.warning_seconds < 60 || policy.warning_seconds > 3600 ||
    policy.stop_seconds < policy.warning_seconds || policy.stop_seconds > 7200 ||
    !['prompt','always','never'].includes(policy.keep_mode)) throw new Error('Idle policy is unavailable.');
  return policy;
}

export function IdleTimePolicyCard({ organizationId }: { organizationId: string | null }) {
  const [policy, setPolicy] = useState<IdlePolicy>({ warning_seconds: 300, stop_seconds: 600, keep_mode: 'prompt' });
  const [saved, setSaved] = useState<IdlePolicy | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [reload, setReload] = useState(0);
  const currentOrganization = useRef(organizationId);
  currentOrganization.current = organizationId;
  useEffect(() => {
    let current = true;
    setSaved(null); setMessage('');
    if (!organizationId) { setBusy(false); setMessage('An organization is required to edit the idle policy.'); return; }
    setBusy(true);
    Promise.resolve(policyRpc('get_idle_time_policy',{ p_organization_id: organizationId })).then(({ data,error }) => {
      if (!current) return;
      if (error) throw error;
      const loaded = readPolicy(data); setPolicy(loaded); setSaved(loaded);
    }).catch(() => { if (current) setMessage('The organization policy could not be loaded. Reconnect and retry.'); })
      .finally(() => { if (current) setBusy(false); });
    return () => { current = false; };
  },[organizationId,reload]);
  const save = async () => {
    if (!saved || !organizationId) return;
    if (!Number.isInteger(policy.warning_seconds) || !Number.isInteger(policy.stop_seconds) || policy.warning_seconds < 60 ||
      policy.warning_seconds > 3600 || policy.stop_seconds < policy.warning_seconds || policy.stop_seconds > 7200) {
      setMessage('Warning must be 1–60 minutes. Stop must be at or after the warning and at most 120 minutes.'); return;
    }
    const patch: Partial<IdlePolicy> = {};
    for (const key of ['warning_seconds','stop_seconds','keep_mode'] as const) {
      if (policy[key] !== saved[key]) Object.assign(patch,{ [key]: policy[key] });
    }
    setBusy(true); setMessage('');
    const requestedOrganization = organizationId;
    try {
      const { data,error } = await policyRpc('set_idle_time_policy',{ p_policy: patch, p_organization_id: organizationId });
      if (requestedOrganization !== currentOrganization.current) return;
      if (error) throw error;
      const confirmed = readPolicy(data); setPolicy(confirmed); setSaved(confirmed);
      setMessage('Organization idle policy saved. Desktop agents refresh it when tracking starts, reconnects, or settings refresh.');
    } catch (_) { if (requestedOrganization === currentOrganization.current) setMessage('The idle policy was not saved. Reconnect and retry.'); }
    finally { if (requestedOrganization === currentOrganization.current) setBusy(false); }
  };
  return <Card>
    <CardHeader><CardTitle>Organization idle policy</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <p className="text-sm text-muted-foreground">Computer-wide input is checked every 30 seconds. Mouse movement counts as activity. Low activity percentages do not reduce payable time.</p>
      <p className="text-sm text-muted-foreground">Once the warning threshold is reached, idle time starts at the last detected input, including the initial inactive period.</p>
      <fieldset disabled={busy || !saved} className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2"><Label htmlFor="idle-policy-warning">Warn after (minutes)</Label>
          <Input id="idle-policy-warning" type="number" min={1} max={60} step={1} value={policy.warning_seconds / 60}
            onChange={event => setPolicy({ ...policy, warning_seconds: Number(event.target.value) * 60 })} /></div>
        <div className="space-y-2"><Label htmlFor="idle-policy-stop">Stop after (minutes)</Label>
          <Input id="idle-policy-stop" type="number" min={policy.warning_seconds / 60} max={120} step={1} value={policy.stop_seconds / 60}
            onChange={event => setPolicy({ ...policy, stop_seconds: Number(event.target.value) * 60 })} /></div>
        <div className="space-y-2"><Label htmlFor="idle-policy-keep">Keep idle time</Label>
          <Select disabled={busy || !saved} value={policy.keep_mode} onValueChange={value => setPolicy({ ...policy, keep_mode: value as IdlePolicy['keep_mode'] })}>
            <SelectTrigger id="idle-policy-keep"><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="prompt">Prompt — employee chooses</SelectItem><SelectItem value="always">Always — keep automatically</SelectItem><SelectItem value="never">Never — discard automatically</SelectItem>
            </SelectContent></Select></div>
      </fieldset>
      <p className="text-sm text-muted-foreground">Prompt offers Keep, Discard, or Reassign on return. Pending and kept time remain payable. Reassign moves only the selected interval to an assigned project. Changes to Keep idle time apply to new idle periods.</p>
      <div role="status" aria-live="polite" className="text-sm">{message}</div>
      <Button onClick={save} disabled={busy || !saved}>{busy ? 'Please wait…' : 'Save idle policy'}</Button>
      {!saved && organizationId && <Button variant="outline" disabled={busy} onClick={() => setReload(value => value + 1)}>Retry loading policy</Button>}
    </CardContent>
  </Card>;
}
